import { DebtProductRowData, ProductDealTerms } from './debtProductTypes';
import { EntityType } from './DebtProductsConstants';
import { isEmpty } from '../../utils/utils';

/**
 * Returns the minimum and maximum numeric values for a given key
 * from an array of objects. Only non-null and numeric values are considered.
 *
 * @param {Array<object>} list - The list of objects to evaluate.
 * @param {string} key - The object key whose numeric values should be used.
 *
 * @returns {{ min: number, max: number }}
 *          An object containing:
 *          - min: the smallest numeric value found (including negative values)
 *          - max: the largest numeric value found
 *          If no valid numeric values exist, both min and max return 0.
 *
 * @example
 * const { min, max } = getMinMax(termList, "maximumRoi");
 */
export const getMinMax = (list: any[], key: string) => {
    const values = list
        .map((item) => {
            const val = item[key];
            // Handle both string and number values
            return typeof val === 'number' ? val : parseFloat(val);
        })
        .filter((v) => !isNaN(v));

    if (values.length === 0) {
        return { min: 0, max: 0 };
    }

    return {
        min: Math.min(...values),
        max: Math.max(...values),
    };
};

/**
 * Generates tooltip content with bullet points for an array of security items
 *
 * @param {Array<{type: string, value: string}>} items - Array of security objects to display in the tooltip
 * @param {object} styles - CSS module styles object containing SecuritiesTooltip and SingleSecurity classes
 * @returns {React.ReactNode} JSX element containing the tooltip content with bullet points
 *
 * @example
 * const tooltipContent = getTooltipContent([{type: 'PDC', value: '1'}], styles);
 */
export const getTooltipContent = (items: { type: string; value: string }[] = [], styles: { [key: string]: string }) => {
    if (isEmpty(items)) return null;
    return (
        <div className={styles.SecuritiesTooltip}>
            {items?.map((item, i) => (
                <div className={styles.SingleSecurity} key={`${item.type}-${i}`}>
                    &bull;&nbsp;&nbsp;{item.type}
                </div>
            ))}
        </div>
    );
};

/**
 * Generates tooltip content with bullet points for an array of string items
 *
 * @param {string[]} items - Array of strings to display in the tooltip
 * @param {object} styles - CSS module styles object containing SecuritiesTooltip and SingleSecurity classes
 * @returns {React.ReactNode} JSX element containing the tooltip content with bullet points
 *
 * @example
 * const tooltipContent = getTooltipContentForStrings(['Item 1', 'Item 2'], styles);
 */
export const getTooltipContentForStrings = (items: string[] = [], styles: { [key: string]: string }) => {
    return (
        <div className={styles.SecuritiesTooltip}>
            {items?.map((item, i) => (
                <div className={styles.SingleSecurity} key={`${item}-${i}`}>
                    &bull;&nbsp;&nbsp;{item}
                </div>
            ))}
        </div>
    );
};

/**
 * Creates DealTerms from row data
 */
export const createDealTerms = (rowData: DebtProductRowData): ProductDealTerms => {
    return {
        maximumDealAmount: parseFloat(rowData?.amount || '0') || 0,
        maximumRoi: parseFloat(rowData?.roi || '0') || 0,
        minimumTenure: parseFloat(rowData?.tenure || '0') || 0,
        security: rowData?.security || [],
        isShared: 0,
    };
};

/**
 * Renders a common shimmer loading effect for mandate summary sections
 *
 * @param {number} itemCount - Number of shimmer items to render
 * @param {object} styles - CSS module styles object containing shimmer classes
 * @returns {React.ReactNode} JSX element containing shimmer items
 *
 * @example
 * const shimmer = renderMandateShimmer(5, styles);
 */
export const renderMandateShimmer = (itemCount: number, styles: { [key: string]: string }) => {
    return (
        <>
            {Array.from({ length: itemCount }).map((_, index) => (
                <div key={index} className={styles.ShimmerItem}>
                    <div className={styles.ShimmerLabel}></div>
                    <div className={styles.ShimmerValue}></div>
                </div>
            ))}
        </>
    );
};

// Find parent product row
export const findProductRow = (rows: DebtProductRowData[], productId: string) =>
    rows.find((row) => row.productId === productId && Array.isArray(row.lenders));

// Find lender row (nested under parent row)
export const findLenderRow = (rows: DebtProductRowData[], productId: string, lenderId: string) => {
    for (const parent of rows) {
        if (parent.productId === productId && Array.isArray(parent.lenders)) {
            const lenderRow = parent.lenders.find((l) => l.lenderId === lenderId && l.productId === productId);
            if (lenderRow) return lenderRow;
        }
    }
    return undefined;
};

// Remove product (and all its lenders) from lenderTerms
export const removeProductFromLenderTerms = (lenderTerms, productId) => {
    const updated = {};

    for (const lenderId in lenderTerms) {
        const products = lenderTerms[lenderId];

        // Remove if present & is not shared
        const filteredProducts = Object.fromEntries(
            (Object.entries(products) as [string, ProductDealTerms][]).filter(([pid, term]) => !(pid === productId && term.isShared === 0)),
        );

        if (Object.keys(filteredProducts).length > 0) {
            updated[lenderId] = filteredProducts;
        }
    }

    return updated;
};

// Toggle lender product inside lenderTerms
export const toggleLenderProduct = (lenderTerms, lenderId, productId, lenderRow) => {
    const result = { ...lenderTerms };

    const lenderProducts = result[lenderId] || {};
    const exists = !!lenderProducts[productId];

    if (exists) {
        // Remove product
        const updated = { ...lenderProducts };
        delete updated[productId];

        if (Object.keys(updated).length === 0) {
            delete result[lenderId];
        } else {
            result[lenderId] = updated;
        }
    } else {
        // Add product
        const newProduct = createDealTerms(lenderRow);
        result[lenderId] = { ...lenderProducts, [productId]: newProduct };
    }

    return result;
};

export const getCalculatingKey = (entityId: string, productId: string, type: EntityType) =>
    type === EntityType.PRODUCT ? `${type}_${entityId}` : `${type}_${entityId}_${productId}`;
